// Crash telemetry: send what the app catches (module error boundary, window errors, unhandled promise rejections) to app_errors.
// Never throws, never blocks the screen, never sends the same crash twice in a minute, and stops after 10 reports a minute.
// Carries no lead or contact data: message, stack, route path without query string, app version, browser.
import { supabase } from './supabase';

const WINDOW_MS = 60 * 1000;
const MAX_PER_WINDOW = 10;
// A stale-chunk error after a deploy (Loading chunk N failed, Loading CSS chunk) is expected: the app already shows New Version Available and offers a Reload, so it is not a bug to triage.
const NOISE = [/ResizeObserver loop/i, /^Script error\.?$/i, /chrome-extension:/i, /moz-extension:/i, /Loading (CSS )?chunk [\w-]+ failed/i, /ChunkLoadError/i];
const recent = new Map(); // fingerprint -> last sent ms
let sentTimes = [];

const clip = (v, n) => (v == null ? null : String(v).slice(0, n));

export function fingerprintOf(message, stack) {
  const top = String(stack || '').split('\n').slice(0, 3).join('|');
  const s = `${message}|${top}`;
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = ((h * 31) + s.charCodeAt(i)) | 0;
  return `e${(h >>> 0).toString(16)}`;
}

export function shouldReport(message, fingerprint, now = Date.now()) {
  if (!message || NOISE.some((rx) => rx.test(message))) return false;
  sentTimes = sentTimes.filter((t) => now - t < WINDOW_MS);
  if (sentTimes.length >= MAX_PER_WINDOW) return false;
  const last = recent.get(fingerprint);
  if (last != null && now - last < WINDOW_MS) return false;
  recent.set(fingerprint, now);
  sentTimes.push(now);
  return true;
}

export function resetReporterForTests() { recent.clear(); sentTimes = []; }

export async function reportError(source, error, extra = {}) {
  try {
    const message = clip((error && error.message) || error, 1000);
    const stack = clip(error && error.stack, 4000);
    const fingerprint = fingerprintOf(message, stack);
    if (!shouldReport(message, fingerprint)) return false;
    const { data } = await supabase.auth.getSession();
    const userId = data && data.session && data.session.user && data.session.user.id;
    if (!userId) return false; // the table only accepts a signed-in user's own rows
    const loc = typeof window !== 'undefined' ? window.location : null;
    const route = loc ? clip(`${loc.pathname}${(loc.hash || '').split('?')[0]}`, 300) : null;
    const { error: insertError } = await supabase.from('app_errors').insert({
      user_id: userId,
      source,
      message,
      stack,
      component_stack: clip(extra.componentStack, 4000),
      route,
      app_version: clip(extra.appVersion || process.env.REACT_APP_GIT_VERSION, 60),
      user_agent: clip(typeof navigator !== 'undefined' ? navigator.userAgent : null, 300),
      fingerprint,
    });
    return !insertError;
  } catch (_) {
    return false; // telemetry must never cause a crash of its own
  }
}

let installed = false;
export function installErrorReporter() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => { reportError('window', e.error || e.message); });
  window.addEventListener('unhandledrejection', (e) => { reportError('promise', e.reason instanceof Error ? e.reason : String(e.reason)); });
}
